import type { InputDOMProps } from "@react-types/shared";
import { clsx } from "clsx";
import {
  type CSSProperties,
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactElement,
  type ReactNode,
  useLayoutEffect,
  useState,
} from "react";
import { type AriaNumberFieldProps, mergeProps, useFocus, useLocale, useNumberField, VisuallyHidden } from "react-aria";
import { useNumberFieldState } from "react-stately";

import { useDOMRef } from "../../hooks.ts";
import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";
import style from "./NumberField.module.css";

export interface NumberFieldProps extends AriaNumberFieldProps, InputDOMProps {
  style?: CSSProperties;
  extraAction?: ReactElement;
}

/**
 * The formatting is not live. Please use NumberFormatField at the moment.
 * Although it's working, it will probably be deprecated
 */
const NumberFieldRender: ForwardRefRenderFunction<HTMLInputElement, NumberFieldProps> = (props, ref) => {
  const inputRef = useDOMRef<HTMLInputElement>(ref);
  const { locale } = useLocale();
  const state = useNumberFieldState({ ...props, label: props.label || props.placeholder, locale });
  const {
    labelProps,
    groupProps,
    inputProps,
    incrementButtonProps,
    decrementButtonProps,
    errorMessageProps,
    descriptionProps,
  } = useNumberField(props, state, inputRef);

  const [isFocus, onFocusChange] = useState(false);
  const { focusProps } = useFocus({ onFocusChange });

  useLayoutEffect(
    () => () => {
      // onChange is only triggered onBlur in the NumberField case.
      // When we change tab, blur is not triggered.
      // @see https://github.com/adobe/react-spectrum/issues/5022
      inputRef.current?.blur();
    },
    // As `inputRef` is an immutable reference, it does not need to be specified explicitly in the dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <div style={props.style} data-testid="number-field">
      <div className={formStyle.input_layout}>
        {!!props.label && (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label {...labelProps}>{props.label}</Label>
            <Spacer />
            {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
            {!props.isRequired && !props.isReadOnly && (
              <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
            )}
          </HStack>
        )}
        <Description
          {...descriptionProps}
          data-testid="number-field-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
          })}
        >
          {props.description}
        </Description>
        <HStack
          spacing={3}
          className={clsx(style.input_group, formStyle.input_layout_column1, {
            [style.disabled]: props.isDisabled,
            [style.focus]: isFocus,
            [style.error]: props.isInvalid,
          })}
          {...groupProps}
        >
          {!props.label && (
            <VisuallyHidden>
              <label {...labelProps}>{props.placeholder}</label>
            </VisuallyHidden>
          )}
          <input
            // do not change to number because we want to have full control over the increment/decrement
            // buttons and appearance, see https://css-tricks.com/numeric-inputs-a-comparison-of-browser-defaults/
            type={"text"}
            className={style.input}
            {...mergeProps(inputProps, focusProps)}
            ref={inputRef}
            autoFocus={props.autoFocus}
          />
          {!props.isDisabled && (
            <VStack spacing={0} className={style.buttons}>
              <TriggerButton
                {...incrementButtonProps}
                icon={"arrow_drop_up"}
                label={""}
                aria-label="Increment"
                className={style.button}
                data-testid="number-field-step-up"
              />
              <TriggerButton
                {...decrementButtonProps}
                icon={"arrow_drop_down"}
                label={""}
                aria-label="Decrement"
                className={style.button}
                data-testid="number-field-step-down"
              />
            </VStack>
          )}
        </HStack>
        {!!props.extraAction && <HStack className={formStyle.input_layout_column2}>{props.extraAction}</HStack>}
        {!!props.errorMessage && !!props.isInvalid && (
          <div
            role="alert"
            {...errorMessageProps}
            className={clsx(formStyle.error_message, formStyle.input_layout_column1)}
            data-testid="number-field-error"
          >
            {props.errorMessage as ReactNode}
          </div>
        )}
      </div>
    </div>
  );
};

export const NumberField = forwardRef(NumberFieldRender);
